<script setup>
// core
import {ref} from "vue";
import {useI18n} from "vue-i18n";

// components
import CallNumberView from "@/components/Telephony/CallNumberView.vue";
import BaseCopyButton from "@/components/ui/BaseCopyButton.vue";
import BaseFadeScroll from "@/components/ui/BaseFadeScroll.vue";
import TariffTime from "@/components/Telephony/TariffTime.vue";

// composables
import useExtensionsLimit from "@/components/Telephony/api/useExtensionsLimit";

const {getExtensionsInfo, isUnlimited} = useExtensionsLimit()


const props = defineProps({
    data: {
        type: Array,
        required: true,
        default: () => []
    }
})

const emit = defineEmits(['openSettingsDialog', 'openSelectSipDialog'])

const {t: $t} = useI18n()
const expandedItems = ref(new Set());

const toggleItem = (id) => {
    if (expandedItems.value.has(id)) {
        expandedItems.value.delete(id);
    } else {
        expandedItems.value.add(id);
    }
}

// same split as ConnectedNumbersStatus
const statusKey = (item) => item.status === 'Activated' ? 'active' : 'requires_activation'

const onSettingsClick = (item) => {
    emit('openSettingsDialog', item)
}

const onSelectSipClick = (item) => {
    emit('openSelectSipDialog', item)
}
</script>

<template>
    <ul class="connected-numbers-m">
        <li
            v-for="(item, i) in data"
            :key="i"
            class="connected-numbers-m__item"
            :class="{ 'is-open': expandedItems.has(i) }"
        >
            <div class="connected-numbers-m__header" @click="toggleItem(i)">
                <CallNumberView :number="item.number"/>

                <div class="connected-numbers-m__right">
                    <span
                        class="connected-numbers-m__dot"
                        :class="statusKey(item)"
                        :title="$t(`telephony.connected_numbers.status.${statusKey(item)}`)"
                    ></span>
                    <i class="connected-numbers-m__arrow bi bi-chevron-down"></i>
                </div>
            </div>

            <!-- stays in the DOM so the height can animate from 0fr to 1fr -->
            <div class="connected-numbers-m__body" :inert="!expandedItems.has(i)">
                <div class="connected-numbers-m__inner">
                    <div class="connected-numbers-m__rows">
                        <div class="connected-numbers-m__row">
                            <span class="item_title">{{ $t('telephony.connected_numbers.columns.3') }}</span>
                            <span class="connected-numbers-m__status" :class="statusKey(item)">
                                {{ $t(`telephony.connected_numbers.status.${statusKey(item)}`) }}
                            </span>
                        </div>

                        <div class="connected-numbers-m__row">
                            <span class="item_title">{{ $t('telephony.connected_numbers.columns.1') }}</span>
                            <span class="tariff">
                                <span class="tariff__name">{{ item.tariff ?? '-' }}</span>
                                <span class="tariff__time"><TariffTime :item="item"/></span>
                            </span>
                        </div>

                        <div class="connected-numbers-m__row">
                            <span class="item_title">{{ $t('telephony.connected_numbers.columns.2') }}</span>
                            <!-- !TODO: paid_until is not in the API response yet -->
                            <span class="connected-numbers-m__value" :class="{ 'is-empty': !item.paid_until }">
                                {{ item.paid_until ?? '-' }}
                            </span>
                        </div>

                        <div class="connected-numbers-m__row">
                            <span class="item_title">{{ $t('telephony.connected_numbers.columns.5') }}</span>
                            <span class="connected-numbers-m__value extensions__count">
                                {{ getExtensionsInfo(item).count }}<template v-if="getExtensionsInfo(item).limit !== null">&nbsp;/&nbsp;<BIconInfinity
                                    v-if="isUnlimited(item)"
                                    class="extensions__infinity"
                                /><template v-else>{{ getExtensionsInfo(item).limit }}</template></template>
                            </span>
                        </div>
                    </div>

                    <div class="connected-numbers-m__foot">
                        <!-- .sip-card__pair from SipExtensionCard -->
                        <div class="connected-numbers-m__key">
                            <b class="item_title --colon">{{ $t('telephony.connected_numbers.columns.4') }}</b>
                            <BaseFadeScroll class="connected-numbers-m__key-value" :text="item.api_key"/>
                            <BaseCopyButton
                                :value="item.api_key"
                                :title="$t('telephony.connected_numbers.copy_api_key')"
                                toast="telephony.sip.copied"
                            />
                        </div>

                        <div class="connected-numbers-m__actions">
                            <button type="button" class="connected-numbers-m__action" @click="onSelectSipClick(item)">
                                <i class="bi bi-headset"></i>
                                SIP
                            </button>
                            <button type="button" class="connected-numbers-m__action" @click="onSettingsClick(item)">
                                <i class="bi bi-gear"></i>
                                {{ $t('telephony.connected_numbers.columns.6') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </li>
    </ul>
</template>

<style scoped lang="scss">
.connected-numbers-m {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;

    // html sets Figtree, which the project does not ship
    font-family: 'Gilroy', sans-serif;

    &__item {
        // the card lays its fields out by its own width, not the window's
        container-type: inline-size;

        background-color: var(--block-color);
        border: 1px solid transparent;
        border-radius: 11px;
        overflow: hidden;
        transition: border-color 0.3s ease;

        &.is-open {
            border-color: var(--general-color);
        }
    }

    &__header {
        padding: 14px 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
        cursor: pointer;

        :deep(.item) {
            gap: 8px;
            font-size: 16px;
            font-weight: 500;
            letter-spacing: 0.05rem;
            white-space: nowrap;
            color: var(--text-color);
        }
    }

    &__right {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    // the status shows in full inside, so the dot gives way once the card opens
    &__dot {
        flex-shrink: 0;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: var(--border-light-color);
        transition: opacity 0.2s, transform 0.2s;

        &.active {
            background-color: var(--success-color);
            box-shadow: 0 0 0 3px #70df7b33;
        }

        .is-open & {
            opacity: 0;
            transform: scale(0.4);
        }
    }

    &__arrow {
        font-size: 12px;
        line-height: 1;
        color: var(--border-color);
        transition: transform 0.3s ease;

        .is-open & {
            transform: rotate(180deg);
        }
    }

    &__body {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.3s ease-out;

        .is-open & {
            grid-template-rows: 1fr;
        }
    }

    &__inner {
        min-height: 0;
        overflow: hidden;
        padding: 0 16px;

        .is-open & {
            padding-bottom: 16px;
        }
    }

    &__rows {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding-top: 12px;
        border-top: 1px solid #e3e0f5;
    }

    &__row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        min-height: 24px;
    }

    &__value {
        font-size: 14px;
        font-weight: 500;
        letter-spacing: 0.05rem;
        text-align: right;
        white-space: nowrap;
        color: var(--text-color);

        &.is-empty {
            color: var(--border-primary-color);
        }
    }

    // the label of the status button in table.scss, without the frame
    &__status {
        font-size: 14px;
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: -0.5px;
        text-transform: uppercase;
        white-space: nowrap;

        &.active {
            color: #70df7b;
        }

        &.requires_activation {
            color: var(--border-color);
        }
    }

    &__foot {
        display: flex;
        flex-direction: column;
        gap: 14px;
        margin-top: 10px;
    }

    &__key {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;

        line-height: 19px;
        font-size: 14px;
        color: var(--text-third-color);

        // only the value gives way: the label and the copy button never wrap
        .item_title {
            flex-shrink: 0;
            white-space: nowrap;
        }
    }

    // BaseFadeScroll fades the key only when it is cut off; it is sized by
    // its text so the copy button stays right after it
    &__key-value {
        flex: 0 1 auto;
        min-width: 0;

        font-weight: 500;
        letter-spacing: 0.05rem;
    }

    &__actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
    }

    // .hiw__connect from SettingsHowItWorks
    &__action {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;

        height: 40px;
        padding: 0 14px;

        border: 1px solid var(--general-color);
        border-radius: 9px;
        background: #ffffff;
        color: var(--general-color);
        cursor: pointer;
        transition: 0.25s;

        font-family: 'Gilroy', sans-serif;
        font-size: 15px;
        font-weight: 600;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        white-space: nowrap;

        i {
            font-size: 16px;
        }

        &:hover {
            background: var(--general-color);
            color: #ffffff;
        }
    }
}

// .item_title from SettingsNumberDialog
.item_title {
    display: flex;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: var(--border-primary-color);

    &.--colon::after {
        content: ":";
    }
}

// .tariff from ConnectedNumbersTable, in the body font like there
.tariff {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    font-family: var(--bs-body-font-family);

    &__name {
        font-weight: 500;
        color: var(--text-second-color);

        &::first-letter {
            text-transform: uppercase;
        }
    }

    &__time {
        font-size: 0.85rem;
        color: var(--text-light-color);
    }
}

.extensions__infinity {
    display: inline-block;
    font-size: 1.1em;
    vertical-align: -0.15em;
}

// fields go two, then four in a row, label above value
@container (min-width: 480px) {
    .connected-numbers-m__rows {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px 20px;
    }

    .connected-numbers-m__row {
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 2px;
    }

    .connected-numbers-m__value {
        text-align: left;
    }

    .tariff {
        align-items: flex-start;
    }
}

// the key and the two equal buttons share one line
@container (min-width: 560px) {
    .connected-numbers-m__foot {
        flex-direction: row;
        align-items: center;
        gap: 20px;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid #e3e0f5;
    }

    .connected-numbers-m__actions {
        flex: 1 0 260px;
    }
}

@container (min-width: 640px) {
    .connected-numbers-m__rows {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}

@media (prefers-reduced-motion: reduce) {
    .connected-numbers-m__body,
    .connected-numbers-m__arrow,
    .connected-numbers-m__dot {
        transition: none;
    }
}
</style>
